import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { BlankSlate, Box, Button } from '@factorialco/deprecated-design-system'
import {
  PerformanceGoalsMetricGroupPageDocument,
  PerformanceGoalsMetricGroupPageMetricsDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { InfiniteLoader, usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { CreateMetricModal } from 'modules/goals/components/CreateMetricModal'
import { MetricList } from 'modules/goals/components/MetricList'
import { ProgressSummary } from 'modules/goals/components/ProgressSummary'
import { UpdateMetricGroupModal } from 'modules/goals/components/UpdateMetricGroupModal'
import { useDeleteMetricGoal } from 'modules/goals/hooks/deleteMetricGroup'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const MetricGroupPage = () => {
  const { id } = useParams<{ id: string }>()

  if (!id) throw new Error('No id provided')

  const { data, loading, error } = useQuery(PerformanceGoalsMetricGroupPageDocument, {
    variables: { id },
  })

  const {
    nodes: metrics,
    loading: loadingMetrics,
    error: errorMetrics,
    data: metricsData,
    controls,
  } = usePaginatedQuery(PerformanceGoalsMetricGroupPageMetricsDocument, {
    variables: { id },
    extractConnection: ({ goals }) => goals?.metricsConnection,
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const openCreateDialog = () => setSearchParams({ modal: 'create' })
  const openEditDialog = () => setSearchParams({ modal: 'edit' })

  const { handleDelete } = useDeleteMetricGoal()

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const metricGroup = data?.goals?.metricGroup
  const summary = metricGroup?.summary
  const policies = metricGroup?._policies
  const goalCycle = metricGroup?.performanceCycles?.[0]

  const canUpdateGoal = policies?.update.can
  const canCreateGoalMetric = policies?.createMetric.can
  const canDeleteGoal = policies?.delete.can
  const canUpdateOrDelete = canUpdateGoal || canDeleteGoal

  const onDelete = async () => {
    if (!metricGroup) return

    await handleDelete(metricGroup, () => {
      navigate({
        pathname: navigation.goals.groups.fullPath(),
      })
    })
  }

  useEffect(() => {
    if (loadingMetrics) return

    track(trackingEvents.goals.viewGoal, {
      number_key_results: metricsData?.goals?.metricsConnection?.totalCount ?? 0,
    })
  }, [loadingMetrics])

  return (
    <ResponsivePage.Body
      loading={loading || loadingMetrics}
      error={error || errorMetrics}
      showHeader={false}
      header={{
        widget: canUpdateOrDelete && (
          <Box flexDirection="row" gap="s8">
            {canCreateGoalMetric && (
              <Button iconName="add" onClick={openCreateDialog}>
                {i18n.t('performance_goals.pages.metric_group_page.create_key_result_button')}
              </Button>
            )}
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center" hideWhenDetached={false}>
                {canUpdateGoal && (
                  <PopoverMenu.TouchableOption
                    title={i18n.t('performance_goals.pages.metric_group_page.edit')}
                    onClick={openEditDialog}
                  />
                )}
                {canDeleteGoal && (
                  <PopoverMenu.TouchableOption
                    danger
                    title={i18n.t('performance_goals.pages.metric_group_page.delete_goal_button')}
                    onClick={onDelete}
                  />
                )}
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        ),
      }}
      status={goalCycle && { label: goalCycle.title }}
    >
      {summary && (
        <Box maxWidth="s400" paddingBottom="s24">
          <ProgressSummary showLabel progress={summary.averageProgress} />
        </Box>
      )}

      {metrics.length === 0 ? (
        <Box
          border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
          padding="s24"
          borderRadius={{ all: 'abs004' }}
        >
          <BlankSlate
            illustration="analytics-graph-lines"
            size="small"
            title={i18n.t('performance_goals.key_results_list.blank_slate.title')}
            description={i18n.t('performance_goals.key_results_list.blank_slate.description')}
          >
            {canCreateGoalMetric && (
              <Button iconName="add" onClick={openCreateDialog}>
                {i18n.t('performance_goals.pages.employee_page.create_button')}
              </Button>
            )}
          </BlankSlate>
        </Box>
      ) : (
        <InfiniteLoader controls={controls}>
          <MetricList
            showAssignee
            metrics={metrics}
            refetchQueriesOnEdit={[PerformanceGoalsMetricGroupPageMetricsDocument]}
          />
        </InfiniteLoader>
      )}
      {/* TODO once we remove ResponsivePage's header, move CreateMetricModal and UpdateMetricGroupModal to the Factorial One GoalHeader component */}
      {searchParams.get('modal') === 'create' && (
        <CreateMetricModal
          showEmployeeSelection
          onClose={() => setSearchParams({})}
          refetchQueries={[PerformanceGoalsMetricGroupPageMetricsDocument]}
          defaultValues={{ metricGroupId: String(id) }}
        />
      )}

      {searchParams.get('modal') === 'edit' && metricGroup && (
        <UpdateMetricGroupModal metricGroup={metricGroup} onClose={() => setSearchParams({})} />
      )}
    </ResponsivePage.Body>
  )
}

export default MetricGroupPage
